import type {
  Citation,
  CitationGroup,
  CitationLocatorKind,
  ParsedNarrative,
} from "./types";

export const CITATION_START = "\u{E200}";
export const CITATION_DELIMITER = "\u{E202}";
export const CITATION_STOP = "\u{E201}";

function tokenRegex(): RegExp {
  return new RegExp(
    `${CITATION_START}cite((?:${CITATION_DELIMITER}[^${CITATION_START}${CITATION_DELIMITER}${CITATION_STOP}]*)+)${CITATION_STOP}`,
    "gu",
  );
}

/** Decode an enriched token body into a {@link Citation}. */
export function decodeCitation(body: string): Citation | null {
  const fields = body.split(CITATION_DELIMITER).slice(1);
  if (fields.length < 7) return null;
  const [evidenceId, sourceLabel, startStr, endStr, kindStr, locatorRaw] =
    fields;
  const citedText = fields.slice(6).join(CITATION_DELIMITER);
  const start = Number(startStr);
  const end = Number(endStr);
  const kind: CitationLocatorKind =
    kindStr === "frame" || kindStr === "timestamp" || kindStr === "line"
      ? kindStr
      : "line";
  return {
    evidenceId,
    sourceLabel: sourceLabel ?? "",
    startSeconds: Number.isFinite(start) ? start : 0,
    endSeconds: Number.isFinite(end) ? end : 0,
    locator: { kind, raw: locatorRaw ?? "" },
    citedText,
  };
}

/**
 * Remove every enriched citation token from text, leaving clean prose. Use this
 * on any surface that renders narrative/report/plan text WITHOUT decoding the
 * tokens into clickable anchors (raw markdown, textarea/contentEditable, copy,
 * export) so the private-use sentinels never surface as box glyphs / raw fields.
 * Surfaces that DO want clickable citations use CitedNarrative / CitedMarkdown.
 */
const ANY_SENTINEL = new RegExp(
  `[${CITATION_START}${CITATION_DELIMITER}${CITATION_STOP}]`,
  "gu",
);

export function stripCitationTokens(text: string | null | undefined): string {
  if (
    !text ||
    !(
      text.includes(CITATION_START) ||
      text.includes(CITATION_DELIMITER) ||
      text.includes(CITATION_STOP)
    )
  ) {
    return text ?? "";
  }
  // First remove well-formed tokens; then sweep any orphaned/truncated sentinels
  // so a malformed token can never paint its private-use codepoints as box glyphs.
  return text.replace(tokenRegex(), "").replace(ANY_SENTINEL, "");
}

const SENTENCE_BOUNDARY = /[.!?]\s|\n/g;

/**
 * Anchor = the clause/sentence ending at the citation marker, bounded below by
 * the previous citation's anchor end so anchors never overlap.
 */
function anchorStartFor(
  cleanText: string,
  anchorEnd: number,
  floor: number,
): number {
  let start = floor;
  const slice = cleanText.slice(0, anchorEnd);
  SENTENCE_BOUNDARY.lastIndex = 0;
  let match: RegExpExecArray | null;
  while ((match = SENTENCE_BOUNDARY.exec(slice)) !== null) {
    const boundaryEnd = match.index + match[0].length;
    if (boundaryEnd > start && boundaryEnd < anchorEnd) start = boundaryEnd;
  }
  while (start < anchorEnd && /\s/.test(cleanText[start])) start += 1;
  return start;
}

/**
 * Strip enriched citation tokens from narrative text and return the clean text
 * plus the clickable anchor groups (one per cited phrase; consecutive markers on
 * the same phrase are merged into a multi-source group).
 */
export function parseCitations(text: string | null | undefined): ParsedNarrative {
  if (!text || !text.includes(CITATION_START)) {
    return { cleanText: text ?? "", groups: [] };
  }

  let cleanText = "";
  let cursor = 0;
  let lastAnchorEnd = 0;
  const groups: CitationGroup[] = [];

  for (const match of text.matchAll(tokenRegex())) {
    const idx = match.index ?? 0;
    cleanText += text.slice(cursor, idx);
    const anchorEnd = cleanText.length;
    cursor = idx + match[0].length;

    const citation = decodeCitation(match[1] ?? "");
    if (!citation) continue;

    const prev = groups[groups.length - 1];
    if (prev && prev.anchorEnd === anchorEnd) {
      prev.citations.push(citation); // another source on the same phrase
    } else {
      const anchorStart = anchorStartFor(cleanText, anchorEnd, lastAnchorEnd);
      groups.push({ anchorStart, anchorEnd, citations: [citation] });
      lastAnchorEnd = anchorEnd;
    }
  }
  cleanText += text.slice(cursor);

  return { cleanText, groups };
}
